import React, { useEffect, useState, useRef } from "react";
import { Switch } from "antd";
import AttachMoneyTwoToneIcon from "@material-ui/icons/AttachMoneyTwoTone";
import CancelButton from "@material-ui/icons/CancelRounded";
import moment from "moment";
import cn from "classnames";
import { Tooltip } from "@material-ui/core";
import ReactLoading from "react-loading";
import PropTypes from "prop-types";
import { startCase } from "lodash";
import TextWrap from "../TextWrap";
import ErrorCode from "../ErrorCode";
import helper from "../../../../../utils/helper";
import showAlert from "../../../../../utils/showAlert";
import userServices from "../../../../../api/userServices";
import styles from "../../../resources/index.module.scss";
import ResolveMailContainer from "../ResolveMailContainer";
import { REPAIR_QUEUE_CODES } from "../../../../../utils/constantData";

const { statusFilters, getName, replaceURLs } = helper;

function MailDetailPreview({
  mailRefId,
  selectedRecord,
  threadCount,
  isInsideModal,
  deleteQuarantinedMail,
  resolveQuarantinedMail,
  openErrorModal,
  openFundTagModal,
  openMailModal,
  userAddedFundTag,
  onAssignedToMe,
  onResolved,
  onStartedWorking,
  onStoppedWorking,
  openResolveHistoryModal,
  userInfo,
  expandHandler,
  tableRef,
  hotQueueRulesData,
  mailLabels,
  labelHandler,
  hotQueue,
}) {
  const [started, setStarted] = useState(false);
  const [hotQueueInstructionOpen, setHotQueueInstructionOpen] = useState(true);

  const [IMDFile, setIMDFile] = useState(selectedRecord?.imd_file);

  const { userId } = userInfo;

  const [leftBoxSize, setLeftBoxSize] = useState(
    tableRef.current ? tableRef.current.clientWidth : 0
  );

  const observer = useRef(null);

  useEffect(() => {
    if (tableRef.current) {
      observer.current = new ResizeObserver(() => {
        setLeftBoxSize(tableRef.current ? tableRef.current.clientWidth : 0);
      });

      observer.current.observe(tableRef.current);

      return () =>
        tableRef.current ? observer.current.unobserve(tableRef.current) : null;
    }
  }, []);

  useEffect(() => {
    if (
      selectedRecord?.resolving_data?.id === userId &&
      selectedRecord.status === statusFilters.startResolving
    ) {
      setStarted(true);
    } else setStarted(false);
    setIMDFile(selectedRecord?.imd_file);
  }, [selectedRecord]);

  function startResolving() {
    const payload = {
      reference_number: selectedRecord.reference_id,
    };
    userServices
      .getStartResolving(payload)
      .then(() => {
        onStartedWorking(selectedRecord.reference_id);
        showAlert("Moved to processing!", "success");
      })
      .catch((error) => {
        showAlert(
          error?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  }

  function endResolving() {
    const payload = {
      reference_number: selectedRecord.reference_id,
    };
    userServices
      .getEndResolving(payload)
      .then(() => {
        onStoppedWorking(selectedRecord.reference_id);
      })
      .catch((error) => {
        setStarted(true);
        showAlert(
          error?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  }

  const onSwitchChange = (checked) => {
    setStarted(checked);
    if (!checked) {
      endResolving();
    } else {
      startResolving();
    }
  };

  function assignToMe() {
    const payload = {
      reference_number: selectedRecord.reference_id,
      self_assign: true,
    };
    userServices.getStartResolving(payload).then(() => {
      onAssignedToMe(selectedRecord.reference_id);
      showAlert("Assigned to you", "success");
    });
  }

  const checkStartWorking = () => {
    if (
      selectedRecord.status === statusFilters.error ||
      selectedRecord.status === statusFilters.hotQueue ||
      selectedRecord.status === statusFilters.NoAttachment ||
      selectedRecord.status === statusFilters.largeFile ||
      selectedRecord.status === statusFilters.startResolving ||
      selectedRecord.status === statusFilters.notAProduct ||
      selectedRecord.status === statusFilters.zipFile ||
      selectedRecord.status === statusFilters.ewka ||
      selectedRecord.status === statusFilters.invalid ||
      selectedRecord.status === statusFilters.inactive ||
      selectedRecord.status === statusFilters.quarantine ||
      selectedRecord.status === statusFilters.review
    )
      if (
        selectedRecord.resolving_data.id === 0 ||
        selectedRecord.resolving_data.id === userId
      ) {
        return (
          <div className={styles.rowShade}>
            <div style={{ display: "flex" }}>
              <img
                style={{ marginRight: 5 }}
                src="/assets/loader-circle.svg"
                alt="loader-circle"
              />
              <div aria-label="start-working">Start Working</div>
            </div>

            <div>
              <Switch
                size="small"
                onChange={onSwitchChange}
                checked={started}
              />
            </div>
          </div>
        );
      }
    return null;
  };

  const checkCurrentUser = () => {
    if (
      selectedRecord.status === statusFilters.startResolving &&
      selectedRecord.resolving_data.id !== userId
    ) {
      return (
        <div className={styles.rowShade}>
          <div style={{ display: "flex" }}>
            <img
              style={{ marginRight: 5 }}
              src="/assets/loader-circle.svg"
              alt="loader-circle"
            />
            <div>Currently Working</div>
          </div>

          <div>{selectedRecord.resolving_data.username}</div>
        </div>
      );
    }
    return null;
  };

  const renderAssignToMe = () => {
    if (
      selectedRecord.status === statusFilters.startResolving &&
      selectedRecord.resolving_data.id !== userId
    ) {
      return (
        <div className={styles.rowShade}>
          <div />
          <div />
          <div
            role="button"
            tabIndex="0"
            onKeyDown={assignToMe}
            className={styles.previewFileDownloadLink}
            onClick={assignToMe}
          >
            Override
          </div>
        </div>
      );
    }
    return null;
  };

  const checkViewHistory = () => {
    if (
      selectedRecord.status !== statusFilters.published &&
      selectedRecord.status !== statusFilters.fdPublished &&
      selectedRecord.resolve_history &&
      selectedRecord?.codes.filter((x) => x[0] === "W3006" || x[0] === "SE5018")
        .length > 0
    ) {
      return (
        <div className={styles.rowShade}>
          <div style={{ display: "flex" }}>
            <img
              style={{ marginRight: 5 }}
              src="/assets/history.svg"
              alt="history"
            />
            <div>Resolve History</div>
          </div>

          <div>
            <span
              role="button"
              tabIndex="0"
              onKeyDown={openResolveHistoryModal}
              className={styles.previewFileDownloadLink}
              onClick={openResolveHistoryModal}
            >
              View History
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderStatus = () => {
    switch (selectedRecord.status) {
      case statusFilters.published && statusFilters.fdPublished:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.greenText]: true,
            })}
          >
            Completed
          </div>
        );
      case statusFilters.error:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.redText]: true,
            })}
          >
            Error
          </div>
        );
      case statusFilters.review:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.redText]: true,
            })}
          >
            Review
          </div>
        );
      case statusFilters.resolved:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.blueText]: true,
            })}
          >
            Resolved
          </div>
        );
      case statusFilters.quarantine:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.lightYellowText]: true,
            })}
          >
            Quarantined
          </div>
        );
      case statusFilters.sendQuarantined:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.lightYellowText]: true,
            })}
          >
            Pending verification
          </div>
        );
      case statusFilters.releaseQuarantined:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.lightYellowText]: true,
            })}
          >
            Released
          </div>
        );
      case statusFilters.hotQueue:
      case statusFilters.largeFile:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.orangeText]: true,
            })}
          >
            Hot Queue
          </div>
        );
      case statusFilters.NoAttachment:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.lightBlueText]: true,
            })}
          >
            EWOA
          </div>
        );
      case statusFilters.historicalPrice:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.violetText]: true,
            })}
          >
            Historical Price
          </div>
        );
      case statusFilters.inactive:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.redText]: true,
            })}
          >
            Inactive Product
          </div>
        );
      case statusFilters.invalid:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.redText]: true,
            })}
          >
            Not In DB
          </div>
        );
      case statusFilters.notAProduct:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.redText]: true,
            })}
          >
            Not A Product
          </div>
        );
      case statusFilters.zipFile:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.redText]: true,
            })}
          >
            Zip File
          </div>
        );
      case statusFilters.ewka:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.lightBlueText]: true,
            })}
          >
            EWKA
          </div>
        );
      default:
        return (
          <div
            className={cn({
              [styles.currentStatus]: true,
              [styles.yellowText]: true,
            })}
          >
            Processing
          </div>
        );
    }
  };

  const getProfileImage = (manualUpload, senderName, mailFrom) => {
    if (manualUpload) {
      return startCase(senderName)
        .split(" ")
        .splice(0, 2)
        .map((name) => name[0])
        .join("")
        .toUpperCase();
    }
    return startCase(getName(mailFrom ?? ""))
      .split(" ")
      .splice(0, 2)
      .map((name) => name[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div
      className={styles.preview}
      data-testid="mail-detail-preview"
      id="preview"
    >
      {/* inner expansion line and button */}
      <div
        role="button"
        tabIndex={0}
        onMouseDown={expandHandler}
        className={styles.expandContainer}
      >
        <div className={styles.expandBtnContainer}>
          <div className={styles.expandBtn}>
            <img
              src={
                leftBoxSize <= 800
                  ? "/assets/white-arrow-right.svg"
                  : "/assets/white-arrow.svg"
              }
              alt="arrow"
            />
          </div>
        </div>
      </div>
      {selectedRecord && (
        <>
          {selectedRecord.status === statusFilters.error && isInsideModal && (
            <>
              <TextWrap text={selectedRecord.api_response} />
              <br />
            </>
          )}
          {!(
            selectedRecord.status === statusFilters.error && isInsideModal
          ) && (
            <>
              <div className={styles.previewHeader}>
                <div
                  style={{
                    flexBasis: "60%",
                    display: "flex",
                    overflow: "hidden",
                  }}
                >
                  <div className={styles.profilePic}>
                    {getProfileImage(
                      selectedRecord?.manual_upload,
                      selectedRecord?.sender_details?.sender_name,
                      selectedRecord?.mail_from
                    )}
                  </div>

                  <div
                    className={styles.previewSubject}
                    title={selectedRecord.subject}
                  >
                    {selectedRecord.subject}
                    <div className={styles.emailDateTime}>
                      {moment.unix(selectedRecord.date).format("MMM DD, HH:mm")}
                    </div>
                  </div>
                </div>
                {!selectedRecord?.manual_upload && (
                  <div className={styles.previewReply}>
                    <div
                      data-testid="preview-reply-btn"
                      role="button"
                      tabIndex="0"
                      className={cn({
                        [styles.hide]: threadCount >= 2,
                      })}
                      onKeyDown={() => openMailModal(true)}
                      onClick={() => openMailModal(true)}
                    >
                      <img src="/assets/reply.svg" alt="Reply" />
                      Reply
                    </div>
                    <div
                      data-testid="preview-thread-btn"
                      role="button"
                      tabIndex="0"
                      onKeyDown={() => openMailModal(false)}
                      onClick={() => openMailModal(false)}
                    >
                      <img src="/assets/chat-teardrop.svg" alt="view thread" />
                      Thread ({threadCount})
                    </div>
                  </div>
                )}
                {/* <div className={styles.previewDate}>
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.58334 0C3.67684 0 2.7907 0.268808 2.03697 0.772431C1.28325 1.27605 0.695789 1.99187 0.348888 2.82937C0.00198589 3.66686 -0.0887794 4.58842 0.0880695 5.4775C0.264918 6.36658 0.701439 7.18325 1.34243 7.82424C1.98342 8.46523 2.80009 8.90175 3.68917 9.0786C4.57825 9.25545 5.49981 9.16468 6.3373 8.81778C7.1748 8.47088 7.89061 7.88342 8.39424 7.1297C8.89786 6.37597 9.16667 5.48983 9.16667 4.58333C9.16667 3.98144 9.04812 3.38544 8.81778 2.82937C8.58745 2.27329 8.24984 1.76803 7.82424 1.34243C7.39864 0.916825 6.89338 0.57922 6.3373 0.348885C5.78123 0.118551 5.18523 0 4.58334 0ZM4.58334 8.25C3.85814 8.25 3.14922 8.03495 2.54624 7.63205C1.94326 7.22916 1.4733 6.6565 1.19578 5.98651C0.918256 5.31651 0.845644 4.57927 0.987123 3.868C1.1286 3.15674 1.47782 2.5034 1.99061 1.99061C2.5034 1.47782 3.15674 1.1286 3.868 0.987121C4.57927 0.845642 5.31651 0.918254 5.98651 1.19578C6.6565 1.4733 7.22916 1.94326 7.63206 2.54624C8.03496 3.14922 8.25 3.85813 8.25 4.58333C8.25 5.55579 7.86369 6.48842 7.17606 7.17606C6.48843 7.86369 5.5558 8.25 4.58334 8.25Z"
                      fill="#79828B"
                    />
                    <path
                      d="M6.41667 4.12508H5.04167V2.75008C5.04167 2.62852 4.99338 2.51195 4.90742 2.42599C4.82147 2.34004 4.70489 2.29175 4.58333 2.29175C4.46178 2.29175 4.3452 2.34004 4.25924 2.42599C4.17329 2.51195 4.125 2.62852 4.125 2.75008V4.58342C4.125 4.70497 4.17329 4.82155 4.25924 4.90751C4.3452 4.99346 4.46178 5.04175 4.58333 5.04175H6.41667C6.53822 5.04175 6.6548 4.99346 6.74076 4.90751C6.82671 4.82155 6.875 4.70497 6.875 4.58342C6.875 4.46186 6.82671 4.34528 6.74076 4.25932C6.6548 4.17337 6.53822 4.12508 6.41667 4.12508Z"
                      fill="#79828B"
                    />
                  </svg>
                  {moment.unix(selectedRecord.date).format("MMM DD")}
                </div> */}
              </div>
              {mailLabels.length > 0 && (
                <div className={styles.labelsTextContainer}>
                  {mailLabels.map((e) => (
                    <div
                      key={e}
                      style={{
                        background: e?.color?.backgroundColor ?? "#E3E9EC",
                        color: e?.color?.textColor ?? "#000000",
                      }}
                      className={styles.labelBox}
                    >
                      {e.label_name}
                      {e.type === "user" && (
                        <CancelButton
                          className={styles.closeIcon}
                          onClick={() => labelHandler(e.id, "remove")}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.previewBody}>
                <div style={{ flexBasis: "80%" }}>
                  {selectedRecord?.manual_upload ? (
                    <TextWrap
                      text={`Manual Upload by ${selectedRecord?.mail_from}`}
                    />
                  ) : (
                    <TextWrap text={selectedRecord.body} />
                  )}
                </div>
                {!selectedRecord.manual_upload && (
                  <div className={styles.openLink}>
                    <a
                      rel="noreferrer"
                      href={
                        userInfo?.gmailURL
                          ? `${userInfo?.gmailURL}/#all/${selectedRecord.message_id}`
                          : null
                      }
                      target="_blank"
                      onClick={() => {
                        if (!userInfo?.gmailURL)
                          showAlert(
                            "You must set Gmail URL in settings page",
                            "warning"
                          );
                      }}
                    >
                      <img
                        src="/assets/open-new-tab.svg"
                        alt="full body link"
                      />
                      Open
                    </a>
                  </div>
                )}
              </div>
              {selectedRecord?.zip_file?.file && (
                <a
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  href={selectedRecord?.zip_file?.file}
                  className={styles.previewFileDownload}
                >
                  <img src="/assets/file-download.svg" alt="file-download" />
                  <div>
                    <div className={styles.text}>
                      {selectedRecord?.zip_file?.name}
                    </div>
                  </div>
                </a>
              )}

              {selectedRecord?.mail_attachment && (
                <a
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  href={selectedRecord.mail_attachment}
                  className={styles.previewFileDownload}
                >
                  <img src="/assets/file-download.svg" alt="file-download" />
                  <div>
                    <div className={styles.text}>
                      {selectedRecord?.mail_attachment
                        ?.split("?")[0]
                        .split("/")
                        .pop()}
                    </div>
                  </div>
                </a>
              )}
            </>
          )}

          {selectedRecord.status === statusFilters.error &&
            selectedRecord.codes.length === 0 && (
              <ErrorCode
                code="NA"
                message={selectedRecord.message}
                status={selectedRecord.status}
              />
            )}
          {selectedRecord?.codes &&
            selectedRecord.codes.length !== 0 &&
            selectedRecord.status !== statusFilters.completedValidation &&
            selectedRecord.status !== statusFilters.processToUpload &&
            selectedRecord?.status !== statusFilters.published &&
            selectedRecord?.status !== statusFilters.fdPublished && (
              <ErrorCode
                code={
                  selectedRecord.codes.sort(
                    (a, b) =>
                      REPAIR_QUEUE_CODES.indexOf(b[0]) -
                      REPAIR_QUEUE_CODES.indexOf(a[0])
                  )[0][0]
                }
                message={
                  selectedRecord.codes.sort(
                    (a, b) =>
                      REPAIR_QUEUE_CODES.indexOf(b[0]) -
                      REPAIR_QUEUE_CODES.indexOf(a[0])
                  )[0][1]
                }
                status={selectedRecord.status}
              />
            )}
          {selectedRecord?.status === statusFilters.published && (
            <ErrorCode
              code="202"
              message="The file has successfully submitted to BCG Gateways"
              status={selectedRecord.status}
            />
          )}
          {selectedRecord?.status === statusFilters.fdPublished && (
            <ErrorCode
              code="202"
              message="The file has successfully created in S3 for Fund Data"
              status={selectedRecord.status}
            />
          )}
          {selectedRecord?.codes && selectedRecord.codes.length > 1 && (
            <div className={styles.viewError}>
              <span
                role="button"
                tabIndex="0"
                onKeyDown={openErrorModal}
                onClick={openErrorModal}
              >
                View
                {` ${selectedRecord?.codes?.length} `}
                more
                {selectedRecord?.status === statusFilters.published ||
                selectedRecord?.status === statusFilters.fdPublished
                  ? " warnings"
                  : " errors"}
              </span>
              <img src="/assets/clarity_pop-out-line.svg" alt="open" />
            </div>
          )}

          <hr className={styles.previewHeaderSeparator} />
          {selectedRecord?.tag_type === "IMD02P001 & Non IMD Unit Prices" &&
            selectedRecord?.cpu_value && (
              <div
                className={cn({
                  [styles.workStatus]: true,
                  [styles.checkDistributionText]: true,
                })}
              >
                Check file for Distributions
              </div>
            )}

          {selectedRecord.status !== statusFilters.published &&
            selectedRecord.status !== statusFilters.fdPublished && (
              <div
                className={cn({
                  [styles.workStatus]: true,
                  [styles.hide]:
                    (!selectedRecord.resolve_history &&
                      selectedRecord.status === statusFilters.resolved) ||
                    selectedRecord.status === statusFilters.sendQuarantined ||
                    selectedRecord.status === statusFilters.releaseQuarantined,
                })}
              >
                {checkStartWorking()}
                {checkCurrentUser()}
                {renderAssignToMe()}
                {checkViewHistory()}
              </div>
            )}

          <table>
            <tbody>
              {selectedRecord.reference_id ? (
                <tr>
                  <td>
                    <img src="/assets/hash.svg" alt="hash" />
                  </td>
                  <td>Reference No</td>
                  <td>{selectedRecord.reference_id}</td>
                </tr>
              ) : null}
              {selectedRecord.date ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.25 3H12.75V2.25C12.75 2.05109 12.671 1.86032 12.5303 1.71967C12.3896 1.57902 12.1989 1.5 12 1.5C11.8011 1.5 11.6103 1.57902 11.4696 1.71967C11.329 1.86032 11.25 2.05109 11.25 2.25V3H6.74997V2.25C6.74997 2.05109 6.67095 1.86032 6.5303 1.71967C6.38965 1.57902 6.19888 1.5 5.99997 1.5C5.80106 1.5 5.61029 1.57902 5.46964 1.71967C5.32899 1.86032 5.24997 2.05109 5.24997 2.25V3H3.74997C3.15323 3 2.58094 3.23705 2.15898 3.65901C1.73702 4.08097 1.49997 4.65326 1.49997 5.25V14.25C1.49997 14.8467 1.73702 15.419 2.15898 15.841C2.58094 16.2629 3.15323 16.5 3.74997 16.5H14.25C14.8467 16.5 15.419 16.2629 15.841 15.841C16.2629 15.419 16.5 14.8467 16.5 14.25V5.25C16.5 4.65326 16.2629 4.08097 15.841 3.65901C15.419 3.23705 14.8467 3 14.25 3ZM15 14.25C15 14.4489 14.921 14.6397 14.7803 14.7803C14.6396 14.921 14.4489 15 14.25 15H3.74997C3.55106 15 3.36029 14.921 3.21964 14.7803C3.07899 14.6397 2.99997 14.4489 2.99997 14.25V9H15V14.25ZM15 7.5H2.99997V5.25C2.99997 5.05109 3.07899 4.86032 3.21964 4.71967C3.36029 4.57902 3.55106 4.5 3.74997 4.5H5.24997V5.25C5.24997 5.44891 5.32899 5.63968 5.46964 5.78033C5.61029 5.92098 5.80106 6 5.99997 6C6.19888 6 6.38965 5.92098 6.5303 5.78033C6.67095 5.63968 6.74997 5.44891 6.74997 5.25V4.5H11.25V5.25C11.25 5.44891 11.329 5.63968 11.4696 5.78033C11.6103 5.92098 11.8011 6 12 6C12.1989 6 12.3896 5.92098 12.5303 5.78033C12.671 5.63968 12.75 5.44891 12.75 5.25V4.5H14.25C14.4489 4.5 14.6396 4.57902 14.7803 4.71967C14.921 4.86032 15 5.05109 15 5.25V7.5Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Date and Time</td>
                  <td>
                    {moment.unix(selectedRecord.date).format("MMM DD, HH:mm")}
                  </td>
                </tr>
              ) : null}
              {selectedRecord.manager ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.375 15.75C3.375 15.75 2.25 15.75 2.25 14.625C2.25 13.5 3.375 10.125 9 10.125C14.625 10.125 15.75 13.5 15.75 14.625C15.75 15.75 14.625 15.75 14.625 15.75H3.375ZM9 9C9.89511 9 10.7536 8.64442 11.3865 8.01149C12.0194 7.37855 12.375 6.52011 12.375 5.625C12.375 4.72989 12.0194 3.87145 11.3865 3.23851C10.7536 2.60558 9.89511 2.25 9 2.25C8.10489 2.25 7.24645 2.60558 6.61351 3.23851C5.98058 3.87145 5.625 4.72989 5.625 5.625C5.625 6.52011 5.98058 7.37855 6.61351 8.01149C7.24645 8.64442 8.10489 9 9 9Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Manager</td>
                  <td>{selectedRecord.manager}</td>
                </tr>
              ) : null}
              {selectedRecord.status ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6032 4.39261C12.7616 4.23288 12.8685 4.01377 12.887 3.78937C12.9054 3.56233 12.85 3.32869 12.722 3.13993C12.6876 3.08845 12.6467 3.03961 12.6032 2.99605L12.6018 2.99473L9.89718 0.290057C9.51042 -0.0967026 8.88342 -0.0967026 8.49665 0.290057C8.10989 0.676829 8.10989 1.30383 8.49665 1.69059L9.51173 2.70565L1.10334 2.70565C0.556862 2.70565 0.113342 3.14919 0.113342 3.69565C0.113342 4.24213 0.556862 4.68565 1.10334 4.68565H9.51173L8.47025 5.72713C8.08349 6.11389 8.08349 6.74089 8.47025 7.12765C8.85702 7.51442 9.48402 7.51442 9.87078 7.12765L12.6032 4.39525C12.6018 4.39393 12.6032 4.39393 12.6032 4.39261Z"
                          fill="#79828B"
                        />
                        <path
                          d="M4.8244 12.0103C4.8244 11.7569 4.72804 11.5035 4.534 11.3107L3.49252 10.2693H11.9009C12.4474 10.2693 12.8909 9.82575 12.8909 9.27927C12.8909 8.73279 12.4474 8.28927 11.9009 8.28927H3.49252L4.5076 7.27421C4.70032 7.08147 4.798 6.82805 4.798 6.57461C4.798 6.32117 4.70164 6.06773 4.5076 5.87501C4.12084 5.48825 3.49384 5.48825 3.10708 5.87501C3.0358 5.94627 2.9632 6.01887 2.89192 6.09017C2.71636 6.26573 2.53948 6.43997 2.36524 6.61685C2.13952 6.84387 1.9138 7.06961 1.68676 7.29531C1.46236 7.51707 1.24456 7.74675 1.01752 7.96719C0.798403 8.18103 0.583244 8.39751 0.377324 8.62586C0.163484 8.86346 0.0578843 9.17366 0.133124 9.4931C0.151604 9.57494 0.183284 9.66206 0.224204 9.73598C0.348284 9.95641 0.552884 10.132 0.729763 10.3102C0.944923 10.528 1.16404 10.7418 1.38184 10.9583C1.64056 11.2157 1.89796 11.4757 2.15536 11.7345C2.37448 11.9509 2.5936 12.1701 2.81272 12.3879C2.91832 12.4935 3.0226 12.5991 3.1282 12.7047C3.12952 12.706 3.13216 12.7086 3.13348 12.7099C3.52024 13.0967 4.14724 13.0967 4.534 12.7099C4.72804 12.5172 4.8244 12.2638 4.8244 12.0103Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Current Status</td>
                  <td>{renderStatus()}</td>
                </tr>
              ) : null}
              {selectedRecord.processed_file ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.62501 3.93748V10.1091C9.62501 11.3283 8.73251 12.4133 7.51918 12.53C7.19505 12.5624 6.86772 12.5264 6.55836 12.4244C6.24899 12.3224 5.96445 12.1567 5.72312 11.9379C5.48179 11.7191 5.28903 11.4521 5.1573 11.1542C5.02557 10.8563 4.95779 10.5341 4.95834 10.2083V2.99831C4.95834 2.23414 5.50668 1.53998 6.26501 1.46414C6.46838 1.44288 6.67395 1.46462 6.86837 1.52795C7.0628 1.59129 7.24173 1.6948 7.39355 1.83177C7.54538 1.96874 7.6667 2.13611 7.74964 2.32301C7.83259 2.50991 7.8753 2.71217 7.87501 2.91664V9.04164C7.87501 9.36248 7.61251 9.62498 7.29168 9.62498C6.97084 9.62498 6.70834 9.36248 6.70834 9.04164V3.93748C6.70834 3.69831 6.51001 3.49998 6.27084 3.49998C6.03168 3.49998 5.83334 3.69831 5.83334 3.93748V8.95998C5.83334 9.72414 6.38168 10.4183 7.14001 10.4941C7.34338 10.5154 7.54895 10.4937 7.74337 10.4303C7.9378 10.367 8.11673 10.2635 8.26855 10.1265C8.42038 9.98955 8.5417 9.82218 8.62464 9.63528C8.70759 9.44838 8.7503 9.24612 8.75001 9.04164V3.01581C8.75001 1.79664 7.85751 0.711644 6.64418 0.594978C6.32014 0.563052 5.993 0.599335 5.68383 0.701489C5.37466 0.803644 5.09031 0.969407 4.84908 1.18811C4.60785 1.40681 4.41509 1.6736 4.2832 1.97131C4.15132 2.26901 4.08324 2.59103 4.08334 2.91664V10.0741C4.08334 11.7483 5.30834 13.2475 6.97668 13.405C8.89584 13.58 10.5 12.0866 10.5 10.2083V3.93748C10.5 3.69831 10.3017 3.49998 10.0625 3.49998C9.82334 3.49998 9.62501 3.69831 9.62501 3.93748Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Processed File</td>
                  <td>
                    <a
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      href={selectedRecord.processed_file}
                    >
                      Download
                    </a>
                  </td>
                </tr>
              ) : null}
              {IMDFile ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.62501 3.93748V10.1091C9.62501 11.3283 8.73251 12.4133 7.51918 12.53C7.19505 12.5624 6.86772 12.5264 6.55836 12.4244C6.24899 12.3224 5.96445 12.1567 5.72312 11.9379C5.48179 11.7191 5.28903 11.4521 5.1573 11.1542C5.02557 10.8563 4.95779 10.5341 4.95834 10.2083V2.99831C4.95834 2.23414 5.50668 1.53998 6.26501 1.46414C6.46838 1.44288 6.67395 1.46462 6.86837 1.52795C7.0628 1.59129 7.24173 1.6948 7.39355 1.83177C7.54538 1.96874 7.6667 2.13611 7.74964 2.32301C7.83259 2.50991 7.8753 2.71217 7.87501 2.91664V9.04164C7.87501 9.36248 7.61251 9.62498 7.29168 9.62498C6.97084 9.62498 6.70834 9.36248 6.70834 9.04164V3.93748C6.70834 3.69831 6.51001 3.49998 6.27084 3.49998C6.03168 3.49998 5.83334 3.69831 5.83334 3.93748V8.95998C5.83334 9.72414 6.38168 10.4183 7.14001 10.4941C7.34338 10.5154 7.54895 10.4937 7.74337 10.4303C7.9378 10.367 8.11673 10.2635 8.26855 10.1265C8.42038 9.98955 8.5417 9.82218 8.62464 9.63528C8.70759 9.44838 8.7503 9.24612 8.75001 9.04164V3.01581C8.75001 1.79664 7.85751 0.711644 6.64418 0.594978C6.32014 0.563052 5.993 0.599335 5.68383 0.701489C5.37466 0.803644 5.09031 0.969407 4.84908 1.18811C4.60785 1.40681 4.41509 1.6736 4.2832 1.97131C4.15132 2.26901 4.08324 2.59103 4.08334 2.91664V10.0741C4.08334 11.7483 5.30834 13.2475 6.97668 13.405C8.89584 13.58 10.5 12.0866 10.5 10.2083V3.93748C10.5 3.69831 10.3017 3.49998 10.0625 3.49998C9.82334 3.49998 9.62501 3.69831 9.62501 3.93748Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>IMD File</td>
                  <td>
                    <a
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      href={IMDFile}
                    >
                      Download
                    </a>
                  </td>
                </tr>
              ) : null}

              {selectedRecord.sheet_name ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.62501 3.93748V10.1091C9.62501 11.3283 8.73251 12.4133 7.51918 12.53C7.19505 12.5624 6.86772 12.5264 6.55836 12.4244C6.24899 12.3224 5.96445 12.1567 5.72312 11.9379C5.48179 11.7191 5.28903 11.4521 5.1573 11.1542C5.02557 10.8563 4.95779 10.5341 4.95834 10.2083V2.99831C4.95834 2.23414 5.50668 1.53998 6.26501 1.46414C6.46838 1.44288 6.67395 1.46462 6.86837 1.52795C7.0628 1.59129 7.24173 1.6948 7.39355 1.83177C7.54538 1.96874 7.6667 2.13611 7.74964 2.32301C7.83259 2.50991 7.8753 2.71217 7.87501 2.91664V9.04164C7.87501 9.36248 7.61251 9.62498 7.29168 9.62498C6.97084 9.62498 6.70834 9.36248 6.70834 9.04164V3.93748C6.70834 3.69831 6.51001 3.49998 6.27084 3.49998C6.03168 3.49998 5.83334 3.69831 5.83334 3.93748V8.95998C5.83334 9.72414 6.38168 10.4183 7.14001 10.4941C7.34338 10.5154 7.54895 10.4937 7.74337 10.4303C7.9378 10.367 8.11673 10.2635 8.26855 10.1265C8.42038 9.98955 8.5417 9.82218 8.62464 9.63528C8.70759 9.44838 8.7503 9.24612 8.75001 9.04164V3.01581C8.75001 1.79664 7.85751 0.711644 6.64418 0.594978C6.32014 0.563052 5.993 0.599335 5.68383 0.701489C5.37466 0.803644 5.09031 0.969407 4.84908 1.18811C4.60785 1.40681 4.41509 1.6736 4.2832 1.97131C4.15132 2.26901 4.08324 2.59103 4.08334 2.91664V10.0741C4.08334 11.7483 5.30834 13.2475 6.97668 13.405C8.89584 13.58 10.5 12.0866 10.5 10.2083V3.93748C10.5 3.69831 10.3017 3.49998 10.0625 3.49998C9.82334 3.49998 9.62501 3.69831 9.62501 3.93748Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Sheet Name</td>
                  <td>{selectedRecord.sheet_name}</td>
                </tr>
              ) : null}
              {selectedRecord.ignored_sheet_names ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.62501 3.93748V10.1091C9.62501 11.3283 8.73251 12.4133 7.51918 12.53C7.19505 12.5624 6.86772 12.5264 6.55836 12.4244C6.24899 12.3224 5.96445 12.1567 5.72312 11.9379C5.48179 11.7191 5.28903 11.4521 5.1573 11.1542C5.02557 10.8563 4.95779 10.5341 4.95834 10.2083V2.99831C4.95834 2.23414 5.50668 1.53998 6.26501 1.46414C6.46838 1.44288 6.67395 1.46462 6.86837 1.52795C7.0628 1.59129 7.24173 1.6948 7.39355 1.83177C7.54538 1.96874 7.6667 2.13611 7.74964 2.32301C7.83259 2.50991 7.8753 2.71217 7.87501 2.91664V9.04164C7.87501 9.36248 7.61251 9.62498 7.29168 9.62498C6.97084 9.62498 6.70834 9.36248 6.70834 9.04164V3.93748C6.70834 3.69831 6.51001 3.49998 6.27084 3.49998C6.03168 3.49998 5.83334 3.69831 5.83334 3.93748V8.95998C5.83334 9.72414 6.38168 10.4183 7.14001 10.4941C7.34338 10.5154 7.54895 10.4937 7.74337 10.4303C7.9378 10.367 8.11673 10.2635 8.26855 10.1265C8.42038 9.98955 8.5417 9.82218 8.62464 9.63528C8.70759 9.44838 8.7503 9.24612 8.75001 9.04164V3.01581C8.75001 1.79664 7.85751 0.711644 6.64418 0.594978C6.32014 0.563052 5.993 0.599335 5.68383 0.701489C5.37466 0.803644 5.09031 0.969407 4.84908 1.18811C4.60785 1.40681 4.41509 1.6736 4.2832 1.97131C4.15132 2.26901 4.08324 2.59103 4.08334 2.91664V10.0741C4.08334 11.7483 5.30834 13.2475 6.97668 13.405C8.89584 13.58 10.5 12.0866 10.5 10.2083V3.93748C10.5 3.69831 10.3017 3.49998 10.0625 3.49998C9.82334 3.49998 9.62501 3.69831 9.62501 3.93748Z"
                          fill="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Ignored Sheet Name</td>
                  <td>{selectedRecord.ignored_sheet_names}</td>
                </tr>
              ) : null}
              {selectedRecord?.tag_type ? (
                <tr>
                  <td>
                    <div>
                      <AttachMoneyTwoToneIcon
                        className={styles.moneyIcon}
                        fontSize="small"
                      />
                    </div>
                  </td>
                  <td>Predicted Fund Tag</td>
                  <td
                    title="Predicted by IA, not entirely accurate."
                    className={styles.infoIcon}
                  >
                    {selectedRecord?.tag_type}
                  </td>
                </tr>
              ) : null}
              <tr>
                <td>
                  <div>
                    <AttachMoneyTwoToneIcon
                      className={styles.moneyIcon}
                      fontSize="small"
                    />
                  </div>
                </td>
                <td
                  role="presentation"
                  onClick={openFundTagModal}
                  className={styles.editIcon}
                >
                  Edit Fund Tag
                </td>
                <td>
                  {userAddedFundTag !== 0 &&
                    userAddedFundTag.map((el, index) => {
                      if (index === 0) return `${el}`;
                      return `, ${el}`;
                    })}
                </td>
              </tr>
              {selectedRecord.status === statusFilters.resolved && (
                <>
                  {selectedRecord.resolved_by ? (
                    <tr>
                      <td>
                        <div>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 128 128"
                            style={{
                              fillRule: "evenodd",
                              clipRule: "evenodd",
                              strokeLinejoin: "round",
                              strokeMiterlimit: 2,
                            }}
                          >
                            <g transform="matrix(0.848087,0,0,0.848087,10.6011,93.5671)">
                              <path
                                d="M74.854,28.32C109.668,28.32 138.428,-0.488 138.428,-35.254C138.428,-70.068 109.619,-98.877 74.805,-98.877C41.992,-98.877 14.6,-73.34 11.621,-41.211C14.258,-41.699 18.018,-41.992 20.898,-41.65C23.877,-68.799 46.728,-89.746 74.805,-89.746C104.98,-89.746 129.346,-65.478 129.346,-35.254C129.346,-21.094 124.023,-8.203 115.137,1.465C109.326,-6.201 94.434,-14.063 74.805,-14.063C68.457,-14.063 62.598,-13.184 57.324,-11.768C58.106,-8.838 58.496,-5.664 58.496,-2.49C58.496,7.031 54.981,15.82 49.17,22.754C57.031,26.318 65.772,28.32 74.854,28.32ZM74.805,-24.805C86.914,-24.707 96.338,-35.059 96.338,-48.535C96.338,-61.231 86.816,-71.777 74.805,-71.777C62.793,-71.777 53.174,-61.231 53.272,-48.535C53.369,-35.059 62.695,-24.902 74.805,-24.805ZM19.092,29.15C36.231,29.15 50.684,14.844 50.684,-2.441C50.684,-19.775 36.426,-34.033 19.092,-34.033C1.807,-34.033 -12.5,-19.727 -12.5,-2.441C-12.5,14.941 1.807,29.15 19.092,29.15ZM19.092,3.027C17.041,3.027 15.625,1.611 15.576,-0.391L15.088,-18.799C15.039,-21.191 16.65,-22.852 19.092,-22.852C21.533,-22.852 23.145,-21.191 23.096,-18.799L22.607,-0.391C22.559,1.611 21.143,3.027 19.092,3.027ZM19.043,17.627C16.26,17.627 13.965,15.332 13.965,12.5C13.965,9.766 16.309,7.471 19.043,7.471C21.875,7.471 24.17,9.814 24.17,12.5C24.121,15.332 21.826,17.627 19.043,17.627Z"
                                style={{ fillRule: "nonzero" }}
                                fill="#79828B"
                              />
                            </g>
                          </svg>
                        </div>
                      </td>
                      <td>Resolved By</td>
                      <td>{selectedRecord.resolved_by}</td>
                    </tr>
                  ) : null}
                  {selectedRecord.resolved_at ? (
                    <tr>
                      <td>
                        <div>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 128 128"
                            style={{
                              fillRule: "evenodd",
                              clipRule: "evenodd",
                              strokeLinejoin: "round",
                              strokeMiterlimit: 2,
                            }}
                          >
                            <g transform="matrix(0.839936,0,0,0.839936,-13.4932,88.4845)">
                              <path
                                d="M16.064,3.418C16.064,15.918 22.461,22.217 35.059,22.217L102.734,22.217C101.172,19.58 100,16.65 99.219,13.574L34.375,13.574C28.125,13.574 24.707,10.254 24.707,3.809L24.707,-54.443C24.707,-60.938 28.125,-64.209 34.375,-64.209L122.754,-64.209C128.955,-64.209 132.471,-60.938 132.471,-54.443L132.471,-36.426C134.131,-36.621 135.498,-36.719 136.963,-36.719C138.33,-36.719 139.746,-36.621 141.162,-36.475L141.162,-73.731C141.162,-86.182 134.766,-92.529 122.119,-92.529L35.059,-92.529C22.461,-92.529 16.064,-86.231 16.064,-73.731L16.064,3.418ZM66.162,-41.748L69.873,-41.748C71.924,-41.748 72.51,-42.334 72.51,-44.385L72.51,-48.047C72.51,-50.098 71.924,-50.635 69.873,-50.635L66.162,-50.635C64.063,-50.635 63.477,-50.098 63.477,-48.047L63.477,-44.385C63.477,-42.334 64.063,-41.748 66.162,-41.748ZM87.354,-41.748L91.065,-41.748C93.115,-41.748 93.75,-42.334 93.75,-44.385L93.75,-48.047C93.75,-50.098 93.115,-50.635 91.065,-50.635L87.354,-50.635C85.303,-50.635 84.668,-50.098 84.668,-48.047L84.668,-44.385C84.668,-42.334 85.303,-41.748 87.354,-41.748ZM108.545,-41.748L112.256,-41.748C114.355,-41.748 114.941,-42.334 114.941,-44.385L114.941,-48.047C114.941,-50.098 114.355,-50.635 112.256,-50.635L108.545,-50.635C106.494,-50.635 105.908,-50.098 105.908,-48.047L105.908,-44.385C105.908,-42.334 106.494,-41.748 108.545,-41.748ZM44.922,-20.898L48.633,-20.898C50.732,-20.898 51.318,-21.436 51.318,-23.486L51.318,-27.197C51.318,-29.199 50.732,-29.785 48.633,-29.785L44.922,-29.785C42.871,-29.785 42.285,-29.199 42.285,-27.197L42.285,-23.486C42.285,-21.436 42.871,-20.898 44.922,-20.898ZM66.162,-20.898L69.873,-20.898C71.924,-20.898 72.51,-21.436 72.51,-23.486L72.51,-27.197C72.51,-29.199 71.924,-29.785 69.873,-29.785L66.162,-29.785C64.063,-29.785 63.477,-29.199 63.477,-27.197L63.477,-23.486C63.477,-21.436 64.063,-20.898 66.162,-20.898ZM87.354,-20.898L91.065,-20.898C93.115,-20.898 93.75,-21.436 93.75,-23.486L93.75,-27.197C93.75,-29.199 93.115,-29.785 91.065,-29.785L87.354,-29.785C85.303,-29.785 84.668,-29.199 84.668,-27.197L84.668,-23.486C84.668,-21.436 85.303,-20.898 87.354,-20.898ZM136.865,34.228C154.053,34.228 168.457,19.922 168.457,2.637C168.457,-14.648 154.199,-28.906 136.865,-28.906C119.58,-28.906 105.322,-14.648 105.322,2.637C105.322,20.02 119.58,34.228 136.865,34.228ZM121.777,7.422C119.775,7.422 118.164,5.811 118.164,3.857C118.164,1.904 119.824,0.293 121.777,0.293L133.594,0.293L133.594,-16.26C133.594,-18.164 135.205,-19.727 137.158,-19.727C139.16,-19.727 140.723,-18.164 140.723,-16.26L140.723,3.857C140.723,5.859 139.16,7.422 137.158,7.422L121.777,7.422ZM44.922,0L48.633,0C50.732,0 51.318,-0.537 51.318,-2.588L51.318,-6.299C51.318,-8.35 50.732,-8.887 48.633,-8.887L44.922,-8.887C42.871,-8.887 42.285,-8.35 42.285,-6.299L42.285,-2.588C42.285,-0.537 42.871,0 44.922,0ZM66.162,0L69.873,0C71.924,0 72.51,-0.537 72.51,-2.588L72.51,-6.299C72.51,-8.35 71.924,-8.887 69.873,-8.887L66.162,-8.887C64.063,-8.887 63.477,-8.35 63.477,-6.299L63.477,-2.588C63.477,-0.537 64.063,0 66.162,0ZM87.354,0L91.065,0C93.115,0 93.75,-0.537 93.75,-2.588L93.75,-6.299C93.75,-8.35 93.115,-8.887 91.065,-8.887L87.354,-8.887C85.303,-8.887 84.668,-8.35 84.668,-6.299L84.668,-2.588C84.668,-0.537 85.303,0 87.354,0Z"
                                style={{ fillRule: "nonzero" }}
                                fill="#79828B"
                              />
                            </g>
                          </svg>
                        </div>
                      </td>
                      <td>Resolved at</td>
                      <td>
                        {moment
                          .unix(selectedRecord.resolved_at)
                          .format("MMM DD, HH:mm")}
                      </td>
                    </tr>
                  ) : null}

                  {selectedRecord.resolve_comments ? (
                    <>
                      <tr
                        className={cn({
                          [styles.commentRowHeader]: isInsideModal,
                        })}
                      >
                        <td rowSpan={isInsideModal ? 2 : 1}>
                          <div>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 128 128"
                              style={{
                                fillRule: "evenodd",
                                clipRule: "evenodd",
                                strokeLinejoin: "round",
                                strokeMiterlimit: 2,
                              }}
                            >
                              <g transform="matrix(0.930581,0,0,0.930581,-14.9493,93.1034)">
                                <path
                                  d="M53.174,32.959C55.859,32.959 57.715,31.543 60.889,28.711L82.91,9.326L126.074,9.326C143.75,9.326 153.613,-0.781 153.613,-18.164L153.613,-67.969C153.613,-85.4 143.75,-95.508 126.074,-95.508L43.603,-95.508C25.879,-95.508 16.064,-85.449 16.064,-67.969L16.064,-18.164C16.064,-0.684 25.879,9.326 43.603,9.326L47.266,9.326L47.266,26.123C47.266,30.273 49.414,32.959 53.174,32.959ZM55.322,23.145L55.322,4.785C55.322,1.807 54.199,0.684 51.27,0.684L43.652,0.684C31.055,0.684 24.707,-6.055 24.707,-18.262L24.707,-67.92C24.707,-80.127 31.055,-86.865 43.652,-86.865L126.025,-86.865C138.477,-86.865 144.971,-80.127 144.971,-67.92L144.971,-18.262C144.971,-6.055 138.477,0.684 126.025,0.684L82.715,0.684C79.639,0.684 78.027,1.172 75.879,3.369L55.322,23.145ZM51.709,-61.035L116.455,-61.035C118.213,-61.035 119.629,-62.451 119.629,-64.258C119.629,-66.016 118.213,-67.432 116.455,-67.432L51.709,-67.432C49.951,-67.432 48.535,-66.016 48.535,-64.258C48.535,-62.451 49.951,-61.035 51.709,-61.035ZM51.709,-40.527L116.455,-40.527C118.213,-40.527 119.629,-41.943 119.629,-43.75C119.629,-45.508 118.213,-46.973 116.455,-46.973L51.709,-46.973C49.951,-46.973 48.535,-45.508 48.535,-43.75C48.535,-41.943 49.951,-40.527 51.709,-40.527ZM51.709,-19.971L94.043,-19.971C95.801,-19.971 97.217,-21.387 97.217,-23.193C97.217,-25 95.801,-26.416 94.043,-26.416L51.709,-26.416C49.951,-26.416 48.535,-25 48.535,-23.193C48.535,-21.387 49.951,-19.971 51.709,-19.971Z"
                                  style={{ fillRule: "nonzero" }}
                                  fill="#79828B"
                                />
                              </g>
                            </svg>
                          </div>
                        </td>
                        <td colSpan={isInsideModal ? 2 : 1}>Comments</td>
                        {!isInsideModal && (
                          <td>{selectedRecord.resolve_comments}</td>
                        )}
                      </tr>
                      {isInsideModal && (
                        <tr className={styles.commentRow}>
                          <td colSpan="2">
                            <TextWrap text={selectedRecord.resolve_comments} />
                          </td>
                        </tr>
                      )}
                    </>
                  ) : null}
                </>
              )}
              {selectedRecord.mime_type &&
              selectedRecord.mime_type !== "Nil" ? (
                <tr>
                  <td>
                    <div>
                      <svg
                        width="18px"
                        height="18px"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 14.5V17.5M12 11.5H12.01M13 3H8.2C7.0799 3 6.51984 3 6.09202 3.21799C5.71569 3.40973 5.40973 3.71569 5.21799 4.09202C5 4.51984 5 5.0799 5 6.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.0799 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V9M13 3L19 9M13 3V7.4C13 7.96005 13 8.24008 13.109 8.45399C13.2049 8.64215 13.3578 8.79513 13.546 8.89101C13.7599 9 14.0399 9 14.6 9H19"
                          stroke="#79828B"
                        />
                      </svg>
                    </div>
                  </td>
                  <td>Mime Type</td>
                  <td>
                    <Tooltip title={selectedRecord.mime_type}>
                      <div className={styles.mimeDiv}>
                        {selectedRecord.mime_type}
                      </div>
                    </Tooltip>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {hotQueue && Object.keys(hotQueueRulesData).length > 0 ? (
            <div>
              <div className={styles.hotqueueInstructionHead}>
                <button
                  type="button"
                  className="drop-btn"
                  onClick={() =>
                    setHotQueueInstructionOpen(!hotQueueInstructionOpen)
                  }
                >
                  <img
                    src={
                      !hotQueueInstructionOpen
                        ? "/assets/hot-queue-dropright.svg"
                        : "/assets/hot-queue-dropdown.svg"
                    }
                    alt="dropdown"
                    className="icon-img"
                  />
                  <div>Hot Queue Notes and Instruction</div>
                </button>
              </div>
              {hotQueueInstructionOpen && (
                <div className={styles.hotqueueIntructionContent}>
                  <h3>{hotQueueRulesData?.rule_name}</h3>
                  {hotQueueRulesData.rule_details.map((e, i) => (
                    <div key={e} className={styles.ruleContainer}>
                      <p className={styles.ruleFont}>
                        Rule {i + 1} : {startCase(e?.condition_param)}{" "}
                        {e?.condition} {e?.condition_data}
                      </p>
                    </div>
                  ))}
                  <p className={styles.underlineFont}>Notes</p>
                  <p className={styles.whiteSpacePre}>
                    {hotQueueRulesData?.hotqueue_notes
                      ? replaceURLs(hotQueueRulesData?.hotqueue_notes)
                      : "--"}
                  </p>
                  <p className={styles.underlineFont}>Instructions</p>
                  <p className={styles.whiteSpacePre}>
                    {hotQueueRulesData?.instruction
                      ? replaceURLs(hotQueueRulesData?.instruction)
                      : "--"}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          {selectedRecord.status === statusFilters.resolved &&
            isInsideModal && <br />}

          {(selectedRecord.dop || selectedRecord.prepared_by) && (
            <div className={styles.previewPreparationDetails}>
              Preparation:
              {selectedRecord.prepared_by && (
                <>&nbsp;{selectedRecord.prepared_by?.trim()}</>
              )}
              {selectedRecord.dop && (
                <>
                  {selectedRecord.prepared_by && ", "}
                  <span style={{ whiteSpace: "nowrap" }}>
                    {moment.unix(selectedRecord.dop).format("Do MMM YYYY")}
                  </span>
                </>
              )}
            </div>
          )}

          {selectedRecord?.tag === "ia" && (
            <div className={styles.generateIAPreview}>
              <div className={styles.redBall} />
              <div className={styles.generateIAText}>IA Generated</div>
            </div>
          )}

          {selectedRecord?.hot_route && (
            <div className={styles.generateIAPreview}>
              <div className={styles.blueBall} />
              <div className={styles.generateIAText}>Client In Hot Queue</div>
            </div>
          )}
          <br />
          <br />

          {selectedRecord?.resolving_data?.id === userId &&
            selectedRecord.status === statusFilters.startResolving && (
              <ResolveMailContainer
                errorCode={
                  selectedRecord?.codes.filter(
                    (x) => x[0] === "SE5018" || x[0] === "SE5028"
                  ).length > 0
                }
                referenceId={selectedRecord.reference_id}
                onResolved={onResolved}
                mailRefId={mailRefId}
                isMail={false}
              />
            )}

          <div className={styles.quarantineBtnWrapper}>
            <button
              type="button"
              className={cn({
                [styles.button]: true,
                [styles.red]: true,
                [styles.hide]:
                  selectedRecord.status !== statusFilters.quarantine &&
                  selectedRecord.status !== statusFilters.sendQuarantined,
              })}
              onClick={() => deleteQuarantinedMail()}
            >
              Delete
            </button>
            {/* <button
              type="button"
              className={cn({
                [styles.button]: true,
                [styles.darkGreen]: true,
                [styles.hide]:
                  selectedRecord.status !== statusFilters.quarantine,
              })}
              onClick={() => resolveQuarantinedMail()}
            >
              Forward To Team
            </button> */}
          </div>
        </>
      )}
    </div>
  );
}

export default MailDetailPreview;

MailDetailPreview.defaultProps = {
  threadCount: 0,
  userAddedFundTag: [],
  isInsideModal: false,
  tableRef: {},
  hotQueueRulesData: {},
  mailLabels: [],
  labelHandler: () => {},
};

MailDetailPreview.propTypes = {
  mailRefId: PropTypes.number.isRequired,
  selectedRecord: PropTypes.instanceOf(Object).isRequired,
  threadCount: PropTypes.number,
  isInsideModal: PropTypes.bool,
  deleteQuarantinedMail: PropTypes.func.isRequired,
  resolveQuarantinedMail: PropTypes.func.isRequired,
  openErrorModal: PropTypes.func.isRequired,
  openFundTagModal: PropTypes.func.isRequired,
  openMailModal: PropTypes.func.isRequired,
  userAddedFundTag: PropTypes.instanceOf(Array),
  onAssignedToMe: PropTypes.func.isRequired,
  onResolved: PropTypes.func.isRequired,
  onStartedWorking: PropTypes.func.isRequired,
  onStoppedWorking: PropTypes.func.isRequired,
  openResolveHistoryModal: PropTypes.func.isRequired,
  userInfo: PropTypes.instanceOf(Object).isRequired,
  expandHandler: PropTypes.func.isRequired,
  tableRef: PropTypes.instanceOf(Object),
  hotQueueRulesData: PropTypes.instanceOf(Object),
  mailLabels: PropTypes.instanceOf(Array),
  labelHandler: PropTypes.func,
  hotQueue: PropTypes.bool.isRequired,
};
